package com.dotdrives.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/** One entry of `config.amounts` — docs/schema.md "Giving forms" config shape. */
@Serializable
data class FormAmount(
    val amount: Double,
    val label: String,
    @SerialName("is_default")
    val isDefault: Boolean = false,
)

/**
 * `config.toggles` — the giving-options switches in the builder's Design tab. `quarterly_giving`
 * (default off) offers a quarterly recurring gift next to monthly; imported legacy forms turn it
 * on because the legacy platform always offered quarterly.
 */
@Serializable
data class FormToggles(
    @SerialName("monthly_giving")
    val monthlyGiving: Boolean = true,
    @SerialName("cover_fees")
    val coverFees: Boolean = true,
    @SerialName("anonymous_gifts")
    val anonymousGifts: Boolean = true,
    @SerialName("custom_amount")
    val customAmount: Boolean = false,
    @SerialName("quarterly_giving")
    val quarterlyGiving: Boolean = false,
)

/** One entry of `config.fields` — docs/schema.md: "not a normalized table... live inside config.fields". */
@Serializable
data class FormField(
    val key: String,
    val type: String,
    val required: Boolean = false,
    val order: Int = 0,
    val options: List<String>? = null,
    val label: String? = null,
)

/** `config.design`. */
@Serializable
data class FormDesign(
    @SerialName("background_color")
    val backgroundColor: String? = null,
    val font: String? = null,
    @SerialName("logo_url")
    val logoUrl: String? = null,
    @SerialName("logo_storage_path")
    val logoStoragePath: String? = null,
    @SerialName("logo_file_name") val logoFileName: String? = null,
    @SerialName("logo_mime_type") val logoMimeType: String? = null,
    @SerialName("logo_size_bytes") val logoSizeBytes: Int? = null,
)

/** `config.receipt` — the Receipts tab. */
@Serializable
data class FormReceipt(
    val subject: String = "",
    val message: String = "",
    @SerialName("include_irs_language")
    val includeIrsLanguage: Boolean = true,
)

/**
 * The full `giving_forms.config` JSONB shape, verbatim from docs/schema.md's "Giving forms"
 * section. This is not a normalized table -- it is mutated in place and re-saved as a whole
 * document on every builder save that touches it.
 */
@Serializable
data class FormConfig(
    val amounts: List<FormAmount> = emptyList(),
    val toggles: FormToggles = FormToggles(),
    val fields: List<FormField> = emptyList(),
    val design: FormDesign = FormDesign(),
    val receipt: FormReceipt = FormReceipt(),
    val story: String = "",
    @SerialName("employer_matches") val employerMatches: List<EmployerMatch> = emptyList(),
)

/** `giving_forms` type check constraint values. */
object GivingFormType {
    const val CAMPAIGN = "campaign"
    const val EVERGREEN = "evergreen"
    const val EVENT = "event"
}

/** `giving_forms` status check constraint values. */
object GivingFormStatus {
    const val DRAFT = "draft"
    const val LIVE = "live"
    const val ARCHIVED = "archived"
}

data class GivingForm(
    val id: String,
    val orgId: String,
    val targetId: String?,
    val targetName: String?,
    val name: String,
    val publicSlug: String,
    val headline: String,
    val type: String,
    val status: String,
    val goalAmount: Double?,
    val showProgressBar: Boolean,
    val closeAt: Instant?,
    val config: FormConfig,
    val version: Int,
    val createdAt: Instant,
    val updatedAt: Instant,
)

data class NewGivingForm(
    val name: String,
    val headline: String,
    val type: String,
    val targetId: String?,
    val publicSlug: String,
    val config: FormConfig,
)

/**
 * Aggregated from `form_submissions` (status='completed') -- no such row exists on
 * `giving_forms` itself. `giftsCount`/`recurringCount` count submissions, not distinct givers.
 */
data class FormStats(
    val formId: String,
    val raisedAmount: Double,
    val giftsCount: Int,
    val recurringCount: Int,
)

/**
 * Org-wide `form_submissions` numbers for the dashboard's "RAISED ONLINE" stat tile, last 30
 * days. The "ACTIVE FORMS" tile needs no repository call -- it is a count over the already
 * -loaded `giving_forms` list, which any member can read. "MONTHLY PARTNERS" is not in here --
 * that is a `giving_subscriptions` concept (an active recurring pledge can be months old), not
 * a 30-day `form_submissions` count -- see [FormsBoardUiState.activePartnersCount].
 */
data class FormOrgStats(
    val raisedLast30Days: Double,
    val giftsLast30Days: Int,
)

/** One row of the builder's live submission feed. */
data class FormSubmission(
    val id: String,
    val formId: String,
    val formVersion: Int,
    val donorName: String,
    val amount: Double,
    val isRecurring: Boolean,
    val status: String,
    val createdAt: Instant,
)
